// Styles
import '@design/normalize.css'

// Vue core
import { createApp } from 'vue'
import { createHead } from '@unhead/vue/client'

// App core
import App from './app'
import router from '@router'
import pinia from '@state/pinia'
import { useNowPlayingStore } from '@state/stores/now-playing'
import { useMemberStore } from '@state/stores/member'
import { useAuthStore } from '@state/stores/auth'
import { useQueueStore } from '@state/stores/queue'
import { useFavoritesStore } from '@state/stores/favorites'
import { registerGlobalComponents } from '@components/_globals'
import './registerServiceWorker'

// Services
import messageService from '@services/message-service'
import servicesPlugin from './plugins/services'
import analyticsPlugin from './plugins/analytics'
import { usePlayerService } from '@composables/use-player-service'
import { useQueueService } from '@composables/use-queue-service'
import { messagesKey } from '@composables/use-messages'

// Directives
import clickOutside from '@directives/clickOutside'
import safeHtml from '@directives/safeHtml'

// App Initialization
const app = createApp(App)

app.use(router)
app.use(pinia)
app.use(servicesPlugin)
app.use(createHead())
app.use(analyticsPlugin)

// Every store that needs warming up at boot is initialised here. `auth` goes first:
// it sets the Bearer token the other two fetch with, and `member` needs the user it
// validated. This replaces the Vuex boot loop, which dispatched `init` for any module
// that defined one.
const authStore = useAuthStore()
authStore.init()
useNowPlayingStore().init()
useMemberStore().init(authStore.currentUser)

registerGlobalComponents(app)

// Vue Directives
app.directive('click-outside', clickOutside)
app.directive('safe-html', safeHtml)

app.provide(messagesKey, messageService)

app.mount('#app')

// Cypress Testing Support
//
// A named surface rather than the app instance: the E2E suite used to reach
// through `__app__.$store` into Vuex, which no longer exists. Expose only what
// the specs actually need, so this stays honest about its own coupling.
if (typeof window !== 'undefined' && window.Cypress) {
    window.__e2e__ = {
        router,
        playerService: app.runWithContext(usePlayerService),
        queueService: app.runWithContext(useQueueService),
        // A read-only snapshot, not the store: specs assert on what the queue
        // holds, and nothing outside the app should be able to write to it.
        getQueue: () => ({ ...useQueueStore().$state }),
        // Favourites need writes, not just reads: the UI that sets them lives
        // on a `:hover`-gated resource cover, which no browser automation can
        // reach. Specs arrange state through these and then assert on what the
        // real Your Library and /radio/favorites pages render.
        favourites: {
            add: (type, id) => useFavoritesStore().add({ type, id }),
            remove: (type, id) => useFavoritesStore().remove({ type, id }),
            toggleChannel: id => useFavoritesStore().toggleChannel(id),
            channelIds: () => [...useFavoritesStore().channelIds],
        },
    }
}
